/**
 * Eventbrite Service
 *
 * Integrates with Eventbrite API v3 to fetch organization events
 * Uses OAuth token for authentication
 */

interface EventbriteEvent {
  id: string
  name: {
    text: string
    html: string
  }
  description: {
    text: string
    html: string
  }
  start: {
    timezone: string
    local: string
    utc: string
  }
  end: {
    timezone: string
    local: string
    utc: string
  }
  url: string
  status: string
  currency: string
  online_event: boolean
  venue_id?: string
  venue?: {
    id: string
    name: string
    address: {
      address_1: string
      address_2?: string
      city: string
      region: string
      postal_code: string
      country: string
    }
  }
  category_id?: string
  subcategory_id?: string
  format_id?: string
  resource_uri: string
  is_series: boolean
  is_series_parent: boolean
  inventory_type: string
  show_remaining: boolean
  password?: string
  capacity?: number
  capacity_is_custom: boolean
  tx_time_limit?: number
  hide_start_date: boolean
  hide_end_date: boolean
  locale: string
  is_locked: boolean
  privacy_setting: string
  is_draft: boolean
  is_ticketed: boolean
  is_reserved_seating: boolean
  source: string
  is_invite_only: boolean
  show_pick_a_seat: boolean
  show_seatmap_thumbnail: boolean
  show_colors_in_seatmap_thumbnail: boolean
  is_organization_page: boolean
  is_contact_sharing_enabled: boolean
  external_ticketing?: {
    external_url: string
    ticketing_provider_name: string
  }
  sales_status: string
  checkout_settings?: any
  supports_attendee_reassignment: boolean
  inventory_uuid: string
  is_externally_ticketed: boolean
  listed: boolean
  shareable: boolean
  invite_only: boolean
  show_remaining_tickets: boolean
  summary: string
  facebook_event_id?: string
  logo_id?: string
  logo?: {
    id: string
    url: string
    crop_mask?: any
    original?: {
      url: string
      width: number
      height: number
    }
    aspect_ratio: string
    edge_color: string
    edge_color_set: boolean
  }
  organizer_id: string
  organizer?: {
    id: string
    name: string
    description: {
      text: string
      html: string
    }
    long_description?: {
      text: string
      html: string
    }
    logo_id?: string
    logo?: {
      id: string
      url: string
      crop_mask?: any
      original?: {
        url: string
        width: number
        height: number
      }
      aspect_ratio: string
      edge_color: string
      edge_color_set: boolean
    }
    resource_uri: string
    num_past_events: number
    num_future_events: number
    twitter: string
    facebook: string
    instagram: string
    website: string
    num_followers: number
  }
  created: string
  changed: string
  published: string
  ticket_availability?: {
    has_available_tickets: boolean
    minimum_ticket_price?: {
      currency: string
      value: number
      major_value: string
      display: string
    }
    maximum_ticket_price?: {
      currency: string
      value: number
      major_value: string
      display: string
    }
    is_sold_out: boolean
    start_sales_date?: {
      timezone: string
      local: string
      utc: string
    }
    waitlist_available: boolean
  }
}

interface EventbriteEventsResponse {
  events: EventbriteEvent[]
  pagination: {
    object_count: number
    page_number: number
    page_size: number
    page_count: number
    has_more_items: boolean
    continuation?: string
  }
}

interface EventbriteOrganizationsResponse {
  organizations: Array<{
    id: string
    name: string
    vertical: string
    parent_id?: string
    sub_vertical?: string
    resource_uri: string
    logo_id?: string
    logo?: any
    image_id?: string
    image?: any
    num_past_events: number
    num_future_events: number
  }>
  pagination: {
    object_count: number
    page_number: number
    page_size: number
    page_count: number
    has_more_items: boolean
  }
}

class EventbriteService {
  // No longer store credentials client-side - all API calls go through Netlify Functions
  private enabled: boolean

  constructor() {
    this.enabled = import.meta.env.VITE_EVENTBRITE_ENABLED === 'true'

    console.log('EventbriteService: Initialized (serverless mode)', {
      enabled: this.enabled
    })
  }

  /**
   * Check if service is configured
   */
  isConfigured(): boolean {
    return this.enabled
  }

  /**
   * Get status for admin display
   */
  getStatus() {
    return {
      configured: this.enabled,
      hasCredentials: true, // Server-side only
      hasOrganizationId: true, // Server-side only
      hasOrganizerId: true, // Server-side only
      enabled: this.enabled,
      connected: this.enabled,
      mode: 'serverless' // Indicates we're using Netlify Functions
    }
  }

  /**
   * Make request to Eventbrite via secure Netlify Function
   * This protects OAuth tokens from being exposed in client-side code
   */
  private async makeRequest<T>(functionPath: string, params?: Record<string, any>): Promise<T> {
    if (!this.isConfigured()) {
      throw new Error('Eventbrite service not configured. Please enable VITE_EVENTBRITE_ENABLED.')
    }

    const url = new URL(`/.netlify/functions/${functionPath}`, window.location.origin)

    // Add query parameters
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.append(key, value.toString())
        }
      })
    }

    console.log('EventbriteService: Making serverless request to:', url.pathname)

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
      console.error('EventbriteService: Serverless function error:', {
        status: response.status,
        statusText: response.statusText,
        error: errorData
      })

      // Handle rate limiting
      if (response.status === 429) {
        const retryAfter = parseInt(response.headers.get('Retry-After') || '60')
        throw new Error(`Rate limit exceeded. Retry after ${retryAfter} seconds.`)
      }

      throw new Error(`Eventbrite API Error: ${response.status} ${errorData.error || response.statusText}`)
    }

    const data = await response.json()
    return data
  }

  /**
   * Test connection by fetching events
   */
  async testConnection(): Promise<{ success: boolean; message: string }> {
    try {
      if (!this.isConfigured()) {
        return {
          success: false,
          message: 'Service not configured. VITE_EVENTBRITE_ENABLED must be set to true.'
        }
      }

      // Test by fetching events (limit to 1 for quick test)
      const data = await this.makeRequest<EventbriteEventsResponse>('eventbrite-events', { limit: 1 })

      if (data && data.events) {
        return {
          success: true,
          message: `Connected successfully (${data.pagination.object_count} total events available)`
        }
      } else {
        return { success: false, message: 'Invalid response from Eventbrite API' }
      }
    } catch (error: any) {
      console.error('EventbriteService: Connection test failed:', error)
      return {
        success: false,
        message: `Connection failed: ${error.message}`
      }
    }
  }

  /**
   * Fetch events from organization via secure Netlify Function
   */
  async getOrganizationEvents(limit: number = 50): Promise<EventbriteEvent[]> {
    try {
      if (!this.isConfigured()) {
        throw new Error('Eventbrite service not configured')
      }

      console.log('EventbriteService: Fetching events via serverless function')

      const allEvents: EventbriteEvent[] = []
      let page = 1
      const pageSize = Math.min(50, limit) // Eventbrite max page size is typically 50

      // Keep fetching until we have all events or reach the limit
      while (allEvents.length < limit) {
        const params = {
          limit: pageSize,
          page: page
        }

        const data = await this.makeRequest<EventbriteEventsResponse>('eventbrite-events', params)

        const events = data.events || []

        if (events.length === 0) {
          break // No more events to fetch
        }

        console.log(`EventbriteService: Page ${page} returned ${events.length} events`)

        allEvents.push(...events)

        // Check if we've reached the limit or there are no more pages
        if (allEvents.length >= limit || !data.pagination.has_more_items) {
          break
        }

        page++
      }

      console.log(`EventbriteService: Total events before deduplication: ${allEvents.length}`)

      // Deduplicate events by ID (prevents same event appearing on multiple pages)
      const uniqueEvents = Array.from(
        new Map(allEvents.map(event => [event.id, event])).values()
      )

      console.log(`EventbriteService: Events after deduplication: ${uniqueEvents.length}`)
      if (uniqueEvents.length < allEvents.length) {
        console.warn(`EventbriteService: Removed ${allEvents.length - uniqueEvents.length} duplicate events`)
      }

      // Filter out invalid/deleted events - only include live, listed events
      const validEvents = uniqueEvents.filter(event => {
        const isValid = event.status === 'live' && event.listed === true
        if (!isValid) {
          console.warn(`EventbriteService: Filtering out event ${event.id} - status: ${event.status}, listed: ${event.listed}`)
        }
        return isValid
      })

      console.log(`EventbriteService: Events after filtering: ${validEvents.length} (removed ${uniqueEvents.length - validEvents.length} invalid/unlisted events)`)

      // Limit to requested amount
      const limitedEvents = validEvents.slice(0, limit)

      console.log(`EventbriteService: Retrieved ${limitedEvents.length} valid events via serverless`)
      return limitedEvents
    } catch (error: any) {
      console.error('EventbriteService: Error fetching events:', error)
      throw new Error(`Failed to fetch events: ${error.message}`)
    }
  }

  /**
   * Get a specific event by ID via secure Netlify Function
   */
  async getEvent(eventId: string): Promise<EventbriteEvent> {
    try {
      if (!this.isConfigured()) {
        throw new Error('Eventbrite service not configured')
      }

      console.log('EventbriteService: Fetching event via serverless:', eventId)

      const data = await this.makeRequest<EventbriteEvent>('eventbrite-event', { eventId })
      return data
    } catch (error: any) {
      console.error('EventbriteService: Error fetching event:', error)
      throw new Error(`Failed to fetch event: ${error.message}`)
    }
  }

  /**
   * Convert Eventbrite event to Class format for compatibility
   */
  convertToClass(event: EventbriteEvent) {
    // Parse dates
    const startDate = new Date(event.start.local)
    const endDate = new Date(event.end.local)

    // Format date and time
    const formatDate = (date: Date) => {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    }

    const formatTime = (date: Date) => {
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })
    }

    // Extract price information
    const getPrice = () => {
      if (event.ticket_availability?.minimum_ticket_price) {
        return parseFloat(event.ticket_availability.minimum_ticket_price.major_value)
      }
      return 0 // Free event or price not available
    }

    return {
      id: `eventbrite_${event.id}`,
      title: event.name.text,
      description: event.description.text || event.summary || '',
      date: formatDate(startDate),
      time: formatTime(startDate),
      duration: Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60)), // Duration in minutes
      capacity: event.capacity || 0,
      enrolled: 0, // We don't have direct access to enrollment count
      price: getPrice(),
      difficulty: 'Beginner', // Default difficulty since Eventbrite doesn't have this concept
      category: 'Art Class', // Default category
      materials: event.description.text ? [event.description.text.substring(0, 100)] : [],
      imageId: event.logo?.id || undefined,
      location: event.venue?.name || (event.online_event ? 'Online' : 'TBD'),
      address: event.venue?.address ?
        `${event.venue.address.address_1}, ${event.venue.address.city}, ${event.venue.address.region}` :
        undefined,
      isActive: event.status === 'live' && event.listed,
      maxParticipants: event.capacity || 20,
      ageRestriction: undefined,
      tags: [event.status, event.online_event ? 'online' : 'in-person'].filter(Boolean),
      isEventbriteEvent: true,
      eventbriteData: {
        id: event.id,
        url: event.url,
        status: event.status,
        startUtc: event.start.utc,
        endUtc: event.end.utc,
        isSoldOut: event.ticket_availability?.is_sold_out || false,
        hasAvailableTickets: event.ticket_availability?.has_available_tickets || false,
        isOnline: event.online_event,
        organizerId: event.organizer_id
      }
    }
  }

  /**
   * Get ticket classes for an event (pricing and availability) via secure Netlify Function
   */
  async getEventTicketClasses(eventId: string) {
    try {
      if (!this.isConfigured()) {
        throw new Error('Eventbrite service not configured')
      }

      console.log('EventbriteService: Fetching ticket classes via serverless:', eventId)

      const data = await this.makeRequest<{ ticket_classes: any[] }>('eventbrite-ticket-classes', { eventId })
      return data.ticket_classes || []
    } catch (error: any) {
      console.error('EventbriteService: Error fetching ticket classes:', error)
      return []
    }
  }

  /**
   * Get the direct Eventbrite URL for an event
   */
  getEventUrl(eventId: string): string {
    return `https://www.eventbrite.com/e/${eventId}`
  }
}

// Create singleton instance
export const eventbriteService = new EventbriteService()

export { EventbriteService }
export type { EventbriteEvent }